import { Component, computed, inject, OnInit, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { sortBy } from 'lodash';
import { concat, Observable, of, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, switchMap, tap, toArray } from 'rxjs/operators';
import { filterAndSort } from 'src/app/search.utils';
import { PanModalComponent } from '../../components/pan-modal/pan-modal.component';
import { possibleLocationRoles } from '../../constants';
import { NotifyService } from '../../service/notify.service';
import { UserAdminService } from '../../service/user-admin.service';
import { UserLoginService } from '../../service/user-login.service';
import { UserSearchService } from '../../service/user-search.service';
import { UserLocationResponse, LocationRole, SelectableLocationRole, UserProfile, UserSearchResult } from '../../types';

@Component({
  selector: 'app-admin-user-management-location',
  templateUrl: './admin-user-management-location.component.html',
  styleUrl: './admin-user-management-location.component.scss',
  standalone: false,
})
export class AdminUserManagementLocationComponent implements OnInit {
  private adminService = inject(UserAdminService);
  private userSearch = inject(UserSearchService);
  private notifications = inject(NotifyService);
  public userLogin = inject(UserLoginService);

  public selectedLocation = signal<UserLocationResponse | undefined>(undefined);

  public locationFilter = signal<string>('');
  public userFilter = signal<string>('');

  public showingCreateUser = signal<boolean>(false);
  public newUserEmail = signal<string>('');
  public newUserName = signal<string>('');

  public hasLoadedUserLocations = signal<boolean>(false);
  public hasLoadedLocationUsers = signal<boolean>(false);

  public locations = signal<UserLocationResponse[]>([]);
  public users = signal<UserProfile[]>([]);

  public modal = viewChild<PanModalComponent>('addUserModal');
  public modalIsSearching = signal<boolean>(false);
  public modalSearchQuery = signal<string>('');
  public modalUser = signal<UserSearchResult | undefined>(undefined);
  public modalRoles = signal<LocationRole[]>([]);
  public modalFoundUsers$: Observable<UserSearchResult[]>;
  public modalTypeaheadInput$ = new Subject<string>();

  public readonly possibleRoles = possibleLocationRoles;

  public locationId = computed(() => this.selectedLocation()?.id.toString());

  public selectableRoles = computed(() =>
    this.possibleRoles.map((role) => ({
      ...role,
      disabled: !this.userLogin.canGrantLocationPermission(
        role.id,
        this.selectedLocation()?.branch_id.toString(),
        this.selectedLocation()?.id.toString(),
      ),
    })),
  );

  public visibleLocations = computed(() => filterAndSort(this.locations(), this.locationFilter()));

  public visibleUsers = computed(() =>
    sortBy(
      this.users().filter(
        (u) =>
          u.name?.toLowerCase().includes(this.userFilter().toLowerCase()) ||
          u.email?.toLowerCase().includes(this.userFilter().toLowerCase()),
      ),
      (u) => u.name?.toLowerCase() || u.email?.toLowerCase(),
    ),
  );

  public canSubmitModal = computed(() => {
    const hasSelectedUser = !!this.modalUser();
    const wantsToCreateUser = this.showingCreateUser() && this.newUserEmail() && this.newUserName();
    const hasSelectedRoles = this.modalRoles()?.length > 0;
    return (hasSelectedUser || wantsToCreateUser) && hasSelectedRoles;
  });

  constructor() {
    this.modalFoundUsers$ = concat(
      of([]),
      this.modalTypeaheadInput$.pipe(
        takeUntilDestroyed(),
        distinctUntilChanged(),
        tap(() => this.modalIsSearching.set(true)),
        switchMap((search) => {
          if (!search || search.length < 3) {
            this.modalIsSearching.set(false);
            return of([]);
          }

          return this.userSearch.searchUsersByBranches(search).pipe(
            catchError(() => of([])),
            tap(() => this.modalIsSearching.set(false)),
          );
        }),
      ),
    );
  }

  ngOnInit() {
    this.loadLocations();
  }

  loadLocations(): void {
    this.hasLoadedUserLocations.set(false);
    this.locations.set([]);

    this.adminService.getMyLocations().subscribe((d) => {
      this.locations.set(d);
      this.hasLoadedUserLocations.set(true);
    });
  }

  loadUsers(location: UserLocationResponse): void {
    this.hasLoadedLocationUsers.set(false);
    this.users.set([]);

    this.adminService.getUsersForLocation(location.id).subscribe((d) => {
      this.users.set(d.items);
      this.hasLoadedLocationUsers.set(true);
    });
  }

  selectLocation(location: UserLocationResponse): void {
    this.userFilter.set('');
    this.selectedLocation.set(location);
    this.loadUsers(location);
  }

  addUserRole(user: UserProfile, role: SelectableLocationRole): void {
    this.adminService.grantLocationRole(user.user_id, this.selectedLocation().id.toString(), role.id).subscribe(() => {
      this.notifications.notify(
        `Successfully granted permission "${role.name}" for user "${user.name}" in location "${this.selectedLocation().name}"`,
      );
    });
  }

  removeUserRole(user: UserProfile, role: SelectableLocationRole): void {
    this.adminService.revokeLocationRole(user.user_id, this.selectedLocation().id.toString(), role.id).subscribe(() => {
      this.notifications.notify(
        `Successfully revoked permission "${role.name}" for user "${user.name}" in location "${this.selectedLocation().name}"`,
      );

      if (user.roles.location[this.locationId()].length === 0) {
        this.users.update((users) => users.filter((u) => u.user_id !== user.user_id));
      }
    });
  }

  tryLoadModalUsers(query: string): void {
    if (query.length < 3) return;
    this.modalTypeaheadInput$.next(query);
  }

  async submitModal(): Promise<void> {
    this.hasLoadedLocationUsers.set(false);
    let chosenUser = this.modalUser();

    if (!chosenUser && this.showingCreateUser() && this.newUserEmail() && this.newUserName()) {
      const createdUser = await this.adminService.createUser(this.newUserName().trim(), this.newUserEmail().trim()).toPromise();
      chosenUser = {
        id: createdUser.user_id,
        name: createdUser.name,
        email: createdUser.email,
      };
    }

    concat(...this.modalRoles().map((role) => this.adminService.grantLocationRole(chosenUser.id, this.locationId(), role)))
      .pipe(toArray())
      .subscribe(() => {
        this.notifications.notify(
          `Successfully added user "${chosenUser.name || chosenUser.email}" to location "${this.selectedLocation().name}"`,
        );
        this.userFilter.set('');
        this.loadUsers(this.selectedLocation());

        this.modalIsSearching.set(false);
        this.modalSearchQuery.set('');

        this.clearInputs();
        this.modal().hide();
      });
  }

  removeUserPermissions(user: UserProfile): void {
    concat(
      ...user.roles.location[this.locationId()].map((role) => this.adminService.revokeLocationRole(user.user_id, this.locationId(), role)),
    )
      .pipe(toArray())
      .subscribe(() => {
        this.notifications.notify(`Successfully removed user "${user.name || user.email}" from location "${this.selectedLocation().name}"`);
        this.loadUsers(this.selectedLocation());
      });
  }

  // Needs to be an arrow function to bind `this` correctly
  // https://stackoverflow.com/questions/65826059/how-to-call-a-function-when-addtag-is-used-in-ng-select
  addNewUser = (newUserString: string) => {
    this.modalUser.set(undefined);
    this.showingCreateUser.set(true);

    const [name] = newUserString.split('@');
    this.newUserName.set(name);
    if (newUserString.includes('@')) {
      this.newUserEmail.set(newUserString);
    }

    // Prevent ng-select from creating a tag
    return false;
  };

  public clearInputs() {
    this.showingCreateUser.set(false);
    this.modalUser.set(undefined);
    this.modalRoles.set(undefined);
    this.newUserEmail.set('');
    this.newUserName.set('');
  }
}
